import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Diamond,
  ShoppingBag,
  ArrowLeft,
  Award,
  Truck,
  Shield,
  Sparkles,
  Check,
  Minus,
  Plus,
  Play,
  MessageCircle,
} from "lucide-react";
import CartDrawer from "../components/CartDrawer";
import ProductInquiryDialog from "../components/ProductInquiryDialog";
import { resolveImageUrl } from "../lib/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem("vk_session_id");
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("vk_session_id", sid);
  }
  return sid;
};

const formatPrice = (n) =>
  typeof n === "number"
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "—";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDiamond, setSelectedDiamond] = useState("lab_grown"); // "lab_grown" | "natural"
  const [showVideo, setShowVideo] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [adding, setAdding] = useState(false);

  const sessionId = getSessionId();

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${id}`);
      setProduct(res.data);
      if (res.data?.gem_variants?.length) {
        setSelectedVariant(res.data.gem_variants[0]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Product not found");
      navigate("/shop");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCartCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      const items = res.data?.items || [];
      setCartCount(items.reduce((sum, i) => sum + (i.quantity || 0), 0));
    } catch {
      setCartCount(0);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchProduct();
    fetchCartCount();
  }, [fetchProduct, fetchCartCount]);

  const hasNaturalUpgrade =
    product && product.natural_price && product.natural_price !== product.lab_grown_price;

  const currentPrice =
    selectedDiamond === "natural" && product?.natural_price
      ? product.natural_price
      : product?.lab_grown_price ?? product?.price ?? 0;

  const diamondSuffix = () => {
    if (!hasNaturalUpgrade) return "";
    return selectedDiamond === "natural" ? " — Natural Diamond" : " — Lab-Grown Diamond";
  };

  const addToCart = async () => {
    if (!product) return;
    setAdding(true);
    try {
      await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        product_id: product.id,
        title: `${product.name}${diamondSuffix()}`,
        price: currentPrice,
        quantity,
        selected_gem_variant: selectedVariant || null,
        diamond_type: hasNaturalUpgrade ? selectedDiamond : null,
        product_image_url: product.image_url,
      });
      toast.success("Added to cart");
      await fetchCartCount();
      setCartOpen(true);
    } catch (err) {
      console.error(err);
      toast.error("Could not add to cart");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080809] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#080809] text-[#F5F5F0]" data-testid="product-detail-page">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#080809]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Diamond className="w-7 h-7 text-primary" />
            <span
              className="text-xl text-primary"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              VK Gems & Jewels
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0]">Home</Link>
            <Link to="/shop" className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0]">Shop</Link>
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 hover:bg-white/5"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-[#080809] text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <button
          onClick={() => navigate("/shop")}
          className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-primary mb-8 transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image / Video */}
          <div className="relative">
            <div className="aspect-square overflow-hidden bg-[#121214] border border-white/10">
              {showVideo && product.video_url ? (
                <video
                  src={resolveImageUrl(product.video_url)}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  data-testid="product-video"
                />
              ) : (
                <img
                  src={resolveImageUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              )}
            </div>
            {product.featured && (
              <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-[#080809] text-xs font-medium uppercase tracking-wider">
                Featured
              </span>
            )}
            {product.video_url && (
              <div className="mt-3 flex gap-2" data-testid="media-toggle">
                <button
                  onClick={() => setShowVideo(false)}
                  className={`flex-1 px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                    !showVideo
                      ? "bg-primary text-[#080809] border-primary"
                      : "border-white/15 text-[#A1A1AA] hover:border-white/40"
                  }`}
                  data-testid="media-photo-btn"
                >
                  Photo
                </button>
                <button
                  onClick={() => setShowVideo(true)}
                  className={`flex-1 px-4 py-2 text-xs uppercase tracking-wider border transition-all flex items-center justify-center gap-2 ${
                    showVideo
                      ? "bg-primary text-[#080809] border-primary"
                      : "border-white/15 text-[#A1A1AA] hover:border-white/40"
                  }`}
                  data-testid="media-video-btn"
                >
                  <Play className="w-3 h-3" /> 360° Video
                </button>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p className="text-xs text-primary uppercase tracking-[0.3em] mb-3" data-testid="product-category">
              {product.gem_type || product.category}
            </p>
            <h1
              className="text-4xl md:text-5xl font-light leading-tight mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <p className="text-[#A1A1AA] leading-relaxed mb-6" data-testid="product-description">
              {product.description}
            </p>

            {/* Material badge */}
            <div className="flex flex-wrap gap-2 mb-8">
              <span className="px-3 py-1 text-xs border border-white/10 text-[#A1A1AA]">
                Material: <span className="text-[#F5F5F0]">{product.material}</span>
              </span>
              {product.gem_type && (
                <span className="px-3 py-1 text-xs border border-white/10 text-[#A1A1AA]">
                  Stone: <span className="text-[#F5F5F0]">{product.gem_type}</span>
                </span>
              )}
              {product.certification && (
                <span className="px-3 py-1 text-xs border border-primary/40 text-primary">
                  {product.certification}
                </span>
              )}
            </div>

            {/* Diamond 4 Cs */}
            {(product.carat || product.cut || product.color || product.clarity) && (
              <div className="mb-6 border border-primary/20 p-4" data-testid="diamond-4cs">
                <p className="text-xs text-primary uppercase tracking-[0.2em] mb-3">Diamond 4 Cs</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {product.carat && (
                    <div>
                      <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Carat</p>
                      <p className="text-sm text-[#F5F5F0]" data-testid="spec-carat">{product.carat}</p>
                    </div>
                  )}
                  {product.cut && (
                    <div>
                      <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Cut</p>
                      <p className="text-sm text-[#F5F5F0]" data-testid="spec-cut">{product.cut}</p>
                    </div>
                  )}
                  {product.color && (
                    <div>
                      <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Color</p>
                      <p className="text-sm text-[#F5F5F0]" data-testid="spec-color">{product.color}</p>
                    </div>
                  )}
                  {product.clarity && (
                    <div>
                      <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">Clarity</p>
                      <p className="text-sm text-[#F5F5F0]" data-testid="spec-clarity">{product.clarity}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Other specs */}
            {(product.weight || product.dimensions) && (
              <div className="mb-6 flex flex-wrap gap-4 text-xs" data-testid="product-specs">
                {product.weight && (
                  <div>
                    <span className="text-[#71717A] uppercase tracking-wider">Weight: </span>
                    <span className="text-[#F5F5F0]" data-testid="spec-weight">{product.weight}</span>
                  </div>
                )}
                {product.dimensions && (
                  <div>
                    <span className="text-[#71717A] uppercase tracking-wider">Dimensions: </span>
                    <span className="text-[#F5F5F0]" data-testid="spec-dimensions">{product.dimensions}</span>
                  </div>
                )}
              </div>
            )}

            {/* Price */}
            <div className="mb-8" data-testid="price-block">
              <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Price</p>
              <p className="text-4xl font-light text-primary" data-testid="current-price">
                {formatPrice(currentPrice)}
              </p>
              {hasNaturalUpgrade && (
                <p className="text-xs text-[#71717A] mt-2">
                  {selectedDiamond === "natural"
                    ? `Lab-grown alternative: ${formatPrice(product.lab_grown_price)} (save ${formatPrice(
                        product.natural_price - product.lab_grown_price
                      )})`
                    : `Natural diamond upgrade: ${formatPrice(product.natural_price)}`}
                </p>
              )}
            </div>

            {/* Diamond type selector */}
            {hasNaturalUpgrade && (
              <div className="mb-8" data-testid="diamond-type-selector">
                <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">
                  Choose Diamond Type
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSelectedDiamond("lab_grown")}
                    className={`relative text-left p-4 border transition-all ${
                      selectedDiamond === "lab_grown"
                        ? "border-primary bg-primary/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid="diamond-option-lab-grown"
                  >
                    {selectedDiamond === "lab_grown" && (
                      <Check className="w-4 h-4 text-primary absolute top-3 right-3" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Lab-Grown</span>
                    </div>
                    <p className="text-xl font-light text-[#F5F5F0]">
                      {formatPrice(product.lab_grown_price)}
                    </p>
                    <p className="text-[10px] text-[#71717A] mt-1 leading-snug">
                      Eco-friendly, identical sparkle, more accessible
                    </p>
                  </button>
                  <button
                    onClick={() => setSelectedDiamond("natural")}
                    className={`relative text-left p-4 border transition-all ${
                      selectedDiamond === "natural"
                        ? "border-primary bg-primary/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid="diamond-option-natural"
                  >
                    {selectedDiamond === "natural" && (
                      <Check className="w-4 h-4 text-primary absolute top-3 right-3" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Diamond className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Natural Diamond</span>
                    </div>
                    <p className="text-xl font-light text-[#F5F5F0]">
                      {formatPrice(product.natural_price)}
                    </p>
                    <p className="text-[10px] text-[#71717A] mt-1 leading-snug">
                      Earth-mined, certified, premium heritage piece
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Gem variants */}
            {product.gem_variants?.length > 0 && (
              <div className="mb-8" data-testid="variant-selector">
                <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">
                  Options
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.gem_variants.map((v) => (
                    <button
                      key={v}
                      onClick={() => setSelectedVariant(v)}
                      className={`px-4 py-2 text-sm transition-all ${
                        selectedVariant === v
                          ? "bg-primary text-[#080809]"
                          : "border border-white/15 text-[#A1A1AA] hover:border-white/40"
                      }`}
                      data-testid={`variant-${v.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-white/15">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="p-3 hover:bg-white/5"
                  data-testid="qty-decrement"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center" data-testid="qty-value">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="p-3 hover:bg-white/5"
                  data-testid="qty-increment"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={addToCart}
                disabled={adding || !product.in_stock}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-primary to-[#8B6914] text-[#080809] font-semibold hover:opacity-90 transition-all disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {adding ? "Adding..." : `Add to Cart — ${formatPrice(currentPrice * quantity)}`}
              </button>
            </div>

            {/* Inquire about this product */}
            <button
              onClick={() => setInquiryOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 mb-8 border border-[#C9A962]/40 text-[#C9A962] hover:bg-[#C9A962]/10 transition-all text-sm uppercase tracking-wider"
              data-testid="inquire-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Have a question? Enquire about this product
            </button>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/5">
              <div className="text-center">
                <Award className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider">IGI/GIA Certified</p>
              </div>
              <div className="text-center">
                <Truck className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider">Insured Shipping</p>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider">Lifetime Warranty</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        sessionId={sessionId}
        onCartUpdate={fetchCartCount}
      />

      <ProductInquiryDialog
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        productName={product?.name}
      />
    </div>
  );
}
